import React, { useEffect, useRef } from 'react';
import { useOverlay } from '../context/OverlayContext';
import '../styles/BubblePhysics.css';

// Global bubble registry for collision detection
window.bubbleRegistry = window.bubbleRegistry || new Map();

// Global image cooldown registry: imageName -> timestamp when cooldown expires
window.imageCooldowns = window.imageCooldowns || new Map();

const COOLDOWN_MS = 60000; // 1 minute cooldown

const getAvailableImage = (allImages, currentImage) => {
  const now = Date.now();
  // Filter to images not on cooldown
  const available = allImages.filter(img => {
    const expiry = window.imageCooldowns.get(img);
    return !expiry || now >= expiry;
  });
  // Pick a random available image (exclude current if possible)
  const candidates = available.length > 1
    ? available.filter(img => img !== currentImage)
    : available;
  if (candidates.length > 0) {
    return candidates[Math.floor(Math.random() * candidates.length)];
  }
  // All on cooldown — pick the one expiring soonest
  let soonest = null;
  let soonestTime = Infinity;
  for (const img of allImages) {
    const expiry = window.imageCooldowns.get(img) || 0;
    if (expiry < soonestTime && img !== currentImage) {
      soonestTime = expiry;
      soonest = img;
    }
  }
  return soonest || allImages[0];
};

const BubblePhysics = ({ imageName, bubbleId, size, initialX, allImages }) => {
  const { bubblesEnabled, openOverlay } = useOverlay();
  const bubbleRef = useRef(null);
  const currentImageRef = useRef(imageName);
  const positionRef = useRef({ x: initialX, y: -size });
  const velocityRef = useRef({ x: (Math.random() - 0.5) * 0.125, y: 0.125 + Math.random() * 0.075 }); // 75% slower initial velocities
  const animationIdRef = useRef(null);

  useEffect(() => {
    const bubble = bubbleRef.current;
    if (!bubble) return;

    // Register this bubble
    window.bubbleRegistry.set(bubbleId, {
      position: positionRef.current,
      velocity: velocityRef.current,
      size: size
    });

    const checkBubbleCollisions = () => {
      const currentBubble = window.bubbleRegistry.get(bubbleId);
      if (!currentBubble) return;

      window.bubbleRegistry.forEach((otherBubble, otherId) => {
        if (otherId === bubbleId) return;

        const dx = currentBubble.position.x - otherBubble.position.x;
        const dy = currentBubble.position.y - otherBubble.position.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const minDistance = (currentBubble.size + otherBubble.size) / 2;

        if (distance < minDistance && distance > 0) {
          // Gentle separation instead of violent bounce
          const overlap = minDistance - distance;
          const angle = Math.atan2(dy, dx);
          
          // Gently push bubbles apart
          const separationForce = overlap * 0.01; // Much gentler separation
          currentBubble.position.x += Math.cos(angle) * separationForce;
          currentBubble.position.y += Math.sin(angle) * separationForce;
          
          // Soft velocity adjustment for rolling effect
          const velocityTransfer = 0.1; // Much softer than before
          const normalX = Math.cos(angle);
          const normalY = Math.sin(angle);
          
          // Rolling/sliding effect - transfer some velocity horizontally
          if (Math.abs(normalY) > 0.5) { // More vertical collision
            currentBubble.velocity.x += normalX * velocityTransfer;
            currentBubble.velocity.y += normalY * velocityTransfer * 0.3; // Less vertical bounce
          } else { // More horizontal collision - rolling
            currentBubble.velocity.x += normalX * velocityTransfer * 0.5;
            currentBubble.velocity.y += normalY * velocityTransfer * 0.1; // Minimal vertical change
          }
          
          // Add friction to simulate rolling
          currentBubble.velocity.x *= 0.98;
          currentBubble.velocity.y *= 0.99;
        }
      });
    };

    const animate = () => {
      if (!bubblesEnabled) {
        animationIdRef.current = requestAnimationFrame(animate);
        return;
      }

      const container = bubble.parentElement;
      const containerRect = container.getBoundingClientRect();

      // Check bubble-to-bubble collisions
      checkBubbleCollisions();

      // Update position
      positionRef.current.x += velocityRef.current.x;
      positionRef.current.y += velocityRef.current.y;

      // Boundary collision - sides (gentler)
      if (positionRef.current.x <= 0 || positionRef.current.x >= containerRect.width - size) {
        velocityRef.current.x *= -0.3; // Much gentler damping
        positionRef.current.x = Math.max(0, Math.min(containerRect.width - size, positionRef.current.x));
      }

      // Gentle gravity
      velocityRef.current.y += 0.001; // Slow falling speed
      
      // Air resistance
      velocityRef.current.x *= 0.999;
      velocityRef.current.y *= 0.9995;

      // Reset if bubble goes too far down
      if (positionRef.current.y > containerRect.height + 100) {
        // Put current image on cooldown and pick a new one
        window.imageCooldowns.set(currentImageRef.current, Date.now() + COOLDOWN_MS);
        const newImage = getAvailableImage(allImages, currentImageRef.current);
        currentImageRef.current = newImage;
        // Update the DOM directly for performance
        const img = bubble.querySelector('img');
        if (img) img.src = `/images/${newImage}`;

        positionRef.current.y = -size;
        positionRef.current.x = Math.random() * (containerRect.width - size);
        velocityRef.current.x = (Math.random() - 0.5) * 0.125; // 75% slower initial velocity
        velocityRef.current.y = 0.075 + Math.random() * 0.075; // 75% slower initial fall
      }

      // Apply position
      bubble.style.transform = `translate(${positionRef.current.x}px, ${positionRef.current.y}px)`;

      animationIdRef.current = requestAnimationFrame(animate);
    };

    animationIdRef.current = requestAnimationFrame(animate);

    return () => {
      window.bubbleRegistry.delete(bubbleId);
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
    };
  }, [bubblesEnabled, size, bubbleId, allImages]);

  return (
    <div
      ref={bubbleRef}
      className="bubble-physics"
      style={{
        width: size,
        height: size,
        position: 'absolute',
        borderRadius: '50%',
        cursor: 'pointer',
        backgroundColor: 'var(--primary-color)',
        boxShadow: '0 0 8px rgba(155, 142, 196, 0.25)'
      }}
      onClick={() => openOverlay(currentImageRef.current)}
    >
      <div className="bubble-image-container">
        <img src={`/images/${currentImageRef.current}`} alt="Wedding moment" loading="lazy" />
      </div>
    </div>
  );
};

export default BubblePhysics;